import { memo, useState } from 'react';

import { ButtonWithLinkStyle, Dialog } from '@capsule/consumer-design-system';

import classNames from 'classnames';
import PropTypes from 'prop-types';
import { compose, withHandlers, withProps } from 'recompact';
import styled from 'styled-components';
import InlineSVG from 'svg-inline-react';

import ellipsis from 'public/dotdotdot.svg';
import idIcon from 'public/unverified-photo-id.svg';
import Button from 'src/app/components/Elements/Button';
import { Card, CardItem } from 'src/app/components/Elements/Card';
import { ID_UPLOAD_STATUS, INSURANCE_STRINGS, IS_MOBILE, PHOTO_UPLOAD_STRINGS, UPLOAD_STRINGS } from 'src/consts';

import styles from './styles.module.scss';

const QrCodeContainer = styled.div`
  border: 1px solid;
  border-color: ${({ theme }) => theme.colors.capsuleGray10};
  border-radius: ${({ theme }) => theme.radii.r100};
  display: flex;
  align-items: center;
  padding: ${({ theme }) => theme.space.s4};
  gap: ${({ theme }) => theme.space.s5};
`;

const StyledImage = styled.img`
  display: block;
  max-width: 88px;
`;

const UploadIDInput = ({
  name,
  tabIndex,
  handleFileUpload,
  uploadFieldID,
  uploadFieldIDCapture,
  handleClickUpload,
  handleClickCapture,
  idVerificationStatus,
  formik,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { INSURANCE } = PHOTO_UPLOAD_STRINGS;
  const isInsuranceUpload = name === INSURANCE_STRINGS.frontCard || name === INSURANCE_STRINGS.backCard;

  // Switches statuses depending on if they are uploading an id for verification
  // or uploading an insurance card.
  const uploadStatuses = isInsuranceUpload ? INSURANCE : PHOTO_UPLOAD_STRINGS.DEFAULT;
  const isFileReadyForUpload = typeof (formik?.values?.[name] ?? false) === 'object';

  let unverifiedString = IS_MOBILE ? uploadStatuses.NOT_UPLOADED_STRING_MOBILE : uploadStatuses.NOT_UPLOADED_STRING;
  if (name === INSURANCE_STRINGS.frontCard) {
    unverifiedString = uploadStatuses.NOT_UPLOADED_FRONT_STRING;
  } else if (name === INSURANCE_STRINGS.backCard) {
    unverifiedString = uploadStatuses.NOT_UPLOADED_BACK_STRING;
  }
  const uploadMessage = {
    [ID_UPLOAD_STATUS['unverified']]: unverifiedString,
    [ID_UPLOAD_STATUS['uploaded']]: uploadStatuses.IS_UPLOADED_STRING,
    [ID_UPLOAD_STATUS['pending']]: unverifiedString,
    [ID_UPLOAD_STATUS['failed']]: uploadStatuses.UPLOAD_FAILED_STRING,
    [ID_UPLOAD_STATUS['verified']]: uploadStatuses.IS_UPLOADED_STRING,
  };

  const uploadIcon = {
    [ID_UPLOAD_STATUS['unverified']]: idIcon,
    [ID_UPLOAD_STATUS['uploaded']]: ellipsis,
    [ID_UPLOAD_STATUS['pending']]: ellipsis,
    [ID_UPLOAD_STATUS['failed']]: null,
    [ID_UPLOAD_STATUS['verified']]: ellipsis,
  };

  const shouldShowButtons =
    idVerificationStatus === ID_UPLOAD_STATUS.unverified ||
    idVerificationStatus === ID_UPLOAD_STATUS.failed ||
    idVerificationStatus === ID_UPLOAD_STATUS.pending;

  return (
    <Card className={styles.component}>
      <CardItem>
        <div className={styles.uploadIconContainer}>
          <div
            className={classNames(styles.uploadIcon, {
              [styles.failed]: idVerificationStatus === ID_UPLOAD_STATUS.failed,
            })}
          >
            {uploadIcon[idVerificationStatus] && <InlineSVG src={uploadIcon[idVerificationStatus]} />}
          </div>
          <p className={isInsuranceUpload && styles.capsuleBlue50}>
            {isFileReadyForUpload ? uploadStatuses.IS_UPLOADED_STRING : uploadMessage[idVerificationStatus]}
          </p>
        </div>
        <input
          name={name}
          className={styles.hideInput}
          onChange={handleFileUpload}
          accept="image/*"
          id={uploadFieldID}
          type="file"
        />
        <input
          name={name}
          className={styles.hideInput}
          onChange={handleFileUpload}
          accept="image/*"
          id={uploadFieldIDCapture}
          type="file"
          capture
        />
        <div className={styles.uploadButtonContainer}>
          {!IS_MOBILE && shouldShowButtons && !isFileReadyForUpload && (
            <Button
              className={styles.button}
              tabIndex={tabIndex}
              onClick={handleClickUpload}
              fixedWidth
              size="small"
              style="secondary"
              block
              disabled={isFileReadyForUpload}
              id={name}
            >
              Upload
            </Button>
          )}
          {IS_MOBILE && shouldShowButtons && !isFileReadyForUpload && (
            <Button
              className={styles.button}
              tabIndex={tabIndex + 1}
              onClick={handleClickCapture}
              fixedWidth
              size="small"
              style="secondary"
              block
              capture
              disabled={isFileReadyForUpload}
              id={name}
            >
              ADD ID
            </Button>
          )}
          {shouldShowButtons && !isFileReadyForUpload && !IS_MOBILE && (
            <ButtonWithLinkStyle onClick={() => setIsModalOpen(true)}>Use the Capsule app</ButtonWithLinkStyle>
          )}
        </div>
      </CardItem>
      <Dialog
        isOpen={isModalOpen}
        onOpenChange={setIsModalOpen}
        title="Download the Capsule app"
        description="The most seamless way to manage your meds"
      >
        <QrCodeContainer>
          <StyledImage
            src="/public/get-app-qr-code.svg"
            alt="QR Code which directs you to download mobile application"
          />
          <p>Scan to download the mobile app </p>
        </QrCodeContainer>
      </Dialog>
    </Card>
  );
};

/**
 * @deprecated Use UploadImageField since it has the newer designs
 */
export const UploadIDInputUnwired = compose(
  withProps({
    uploadFieldID: UPLOAD_STRINGS.uploadFieldID,
    uploadFieldIDCapture: UPLOAD_STRINGS.uploadFieldIDCapture,
    tabIndex: null,
  }),
  withHandlers({
    handleClickUpload:
      ({ uploadFieldID }) =>
      async e => {
        e.preventDefault();
        event.stopPropagation();
        document.querySelector(`#${uploadFieldID}`).click();
      },
    handleClickCapture:
      ({ uploadFieldIDCapture }) =>
      async e => {
        e.preventDefault();
        event.stopPropagation();
        document.querySelector(`#${uploadFieldIDCapture}`).click();
      },
  }),
  memo
)(UploadIDInput);

UploadIDInputUnwired.propTypes = {
  idVerificationStatus: PropTypes.oneOf([
    ID_UPLOAD_STATUS.unverified,
    ID_UPLOAD_STATUS.uploaded,
    ID_UPLOAD_STATUS.pending,
    ID_UPLOAD_STATUS.failed,
    ID_UPLOAD_STATUS.verified,
  ]).isRequired,
  className: PropTypes.string,
  name: PropTypes.string.isRequired,
  tabIndex: PropTypes.number,
  handleFileUpload: PropTypes.func,
};

UploadIDInputUnwired.defaultProps = {
  idVerificationStatus: ID_UPLOAD_STATUS.unverified,
};
